//Applications.tsx
import React, {useEffect, useRef, useState} from 'react'
import {changeTextCamal, changeTextCapital} from '../../../_metronic/helpers'
import {Button, Center} from '@mantine/core'
import {useAuth} from '../../modules/auth'
import {
  formatCreateAt,
  formatDates,
  getLeave,
  handleLeaveActions,
} from './LeaveFunctions'
import {Modal} from 'react-bootstrap'
import {truncateString} from '../attendance/ams'
import {InputErrorType} from '../../types/OtherTypes'
import SubmitCancleButton from '../../components/SubmitCancelButton'
import CustomTextInput from '../../components/CustomInput/CustomTextInput'
import Select2 from '../../components/select2'
import ListPagination from '../../components/ListPagination'
import {getUserData, getTeamMemberData} from '../../CommonFunction'
import {useLocation} from 'react-router-dom'

const Applications = ({getLeaveBalances, setBalance}) => {
  const [reasonRemarkModalOpen, setReasonRemarkModalOpen] = useState<string | null>(null)
  const [reasonModalOpen, setReasonModalOpen] = useState<InputErrorType | null>(null)
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<InputErrorType>({})
  const [submitAPIStatus, setSubmitAPIStatus] = useState({
    loading: false,
  })
  const [selectedColumn, setSelectedColumn] = useState('')
  const [showRow, setShowRow] = useState<number[]>([])
  const [hasLoadedLeaves, setHasLoadedLeaves] = useState(false)
  const [requestedLeaveUnavailable, setRequestedLeaveUnavailable] = useState(false)
  const location = useLocation()
  const checkArrayElement = (element: number) => {
    setShowRow((prevShowRow) => {
      const updatedShowRow = [...prevShowRow]
      const index = updatedShowRow.indexOf(element)
      if (index !== -1) {
        updatedShowRow.splice(index, 1)
      } else {
        updatedShowRow.push(element)
      }
      return updatedShowRow
    })
  }
  const {currentUser} = useAuth()
  const [leave, setLeave] = useState<any[]>([])
  const [allLeaves, setAllLeaves] = useState<any[]>([])
  const [userOptions, setUserOptions] = useState<any[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string>('all')
  //Search added by Search on Name and Employee code
  const [searchText, setSearchText] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [pagination, setPagination] = useState({page: 1, pageSize: 10, total: 0, totalPages: 0})
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({})
  const leaveRequestVersion = useRef(0)

  const [apiStatus, setAPIStatus] = useState<{loading: boolean; error: string | null; submitLoading: boolean}>({
    loading: false,
    error: null,
    submitLoading: false,
  })

  const fetchData = (page = pagination.page) => {
    if (!currentUser?.id) return
    const requestVersion = ++leaveRequestVersion.current
    const requestId = new URLSearchParams(location.search).get('requestId') || undefined
    getLeave(currentUser, (data: any[]) => {
      const rows = Array.isArray(data) ? data : []
      const sortedRows = rows.sort((a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime())
      setAllLeaves(sortedRows)
      setHasLoadedLeaves(true)
    }, setAPIStatus, 'receiver', {
      page,
      pageSize: pagination.pageSize,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
      search: searchText.trim() || undefined,
      applicantId: selectedUserId === 'all' ? undefined : selectedUserId,
      leaveId: requestId,
      isCurrent: () => leaveRequestVersion.current === requestVersion,
      onPageInfo: (pageInfo, counts) => {
        setPagination((previous) => ({...previous, ...pageInfo}))
        setStatusCounts(counts)
      },
    })
  }

  useEffect(() => {
    {
      // Load users list: if RM/HR then team members; else all active users
      if (currentUser?.isRmOrHr && currentUser?.id) {
        getTeamMemberData((items: any[]) => setUserOptions(items || []), currentUser.id)
      } else {
        getUserData(setUserOptions)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setShowRow([])
    setHasLoadedLeaves(false)
    setRequestedLeaveUnavailable(false)
    fetchData(pagination.page)
    return () => {
      leaveRequestVersion.current += 1
    }
  }, [currentUser?.id, pagination.page, pagination.pageSize, selectedUserId, selectedStatus, searchText, location.search])

  useEffect(() => {
    let updatedData = [...allLeaves].sort(
      (a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    )

    //Filter by selected user
    if (selectedUserId !== 'all') {
      updatedData = updatedData.filter((row: any) => {
        const applicantId =
          row?.user?.id || row?.sender?.id || row?.userId || row?.senderId || ''
        return applicantId === selectedUserId
      })
    }
    if (selectedStatus !== 'all') {
      updatedData = updatedData.filter(
        (row: any) =>
          row?.status?.toUpperCase() === selectedStatus.toUpperCase()
      )
    }

    // ✅ Search filter (Name + Employee Code)
    if (searchText.trim() !== '') {
      const search = searchText.toLowerCase()

      updatedData = updatedData.filter((row: any) => {
        const fullName = `${row?.user?.firstName ?? ''} ${row?.user?.lastName ?? ''}`.toLowerCase()
        const empCode = (row?.user?.employeeCode ?? '').toLowerCase()

        return fullName.includes(search) || empCode.includes(search)
      })
    }

    // ✅ Sort by latest date
    updatedData.sort(
      (a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    )
    setLeave(updatedData)
    }, [selectedUserId, allLeaves, searchText, selectedStatus])

  useEffect(() => {
    const requestId = new URLSearchParams(location.search).get('requestId')
    if (!requestId || !hasLoadedLeaves) return

    const rowIndex = leave.findIndex((row: any) => row?.id === requestId)
    if (rowIndex < 0) {
      setRequestedLeaveUnavailable(true)
      return
    }

    setRequestedLeaveUnavailable(false)
    setShowRow([rowIndex])
    requestAnimationFrame(() => {
      document.getElementById(`leave-application-${requestId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    })
  }, [hasLoadedLeaves, leave, location.search])

  const countableTotal = Object.values(statusCounts).reduce((total, count) => total + count, 0)
  const statusCount = (status: string) => statusCounts[status] || 0

    if (!currentUser) {
      return (
        <div>
          <Center>Loading...</Center>
        </div>
      )
    }
  return (
    <>
      <div>
        {/* modal to see the reason */}
        <Modal
          show={reasonRemarkModalOpen !== null}
          onClose={() => setReasonRemarkModalOpen(null)}
          animation={false}
          backdrop='static'
          centered
        >
          <div className='modal-header py-3'>
            <h2 className='fw-bolder'>{selectedColumn}</h2>
            <div
              className='btn btn-icon btn-sm btn-active-icon-danger'
              data-kt-users-modal-action='close'
              style={{cursor: 'pointer'}}
              onClick={() => {
                setReasonRemarkModalOpen(null)
              }}
            >
              <i className='fa-solid fa-xmark fs-1' />
            </div>
          </div>
          <div className='card-body'>
            <div
              className='my-5 ms-5'
              dangerouslySetInnerHTML={{__html: reasonRemarkModalOpen ?? ''}}
            />
          </div>
        </Modal>
        <Modal show={reasonModalOpen !== null} animation={false} backdrop='static' centered>
          <div className='modal-header'>
            <h2 className='fw-bolder'>Remark</h2>
            <div
              className='btn btn-icon btn-sm btn-active-icon-danger'
              data-kt-users-modal-action='close'
              style={{cursor: 'pointer'}}
              onClick={() => {
                setError({})
                setReason('')
                setReasonModalOpen(null)
              }}
            >
              <i className='fa-solid fa-xmark fs-1' />
            </div>
          </div>
          <div className='m-3'>
            <div className='row ms-3'>
              <div className='fv-row me-6 col-md-10 col-10 mb-3'>
                <CustomTextInput
                  value={reason}
                  onChange={(event) => setReason(event?.target?.value)}
                  // label='Remark'
                  error={error.reason}
                />
              </div>
            </div>
            <SubmitCancleButton
              cancle={{
                onClick: () => {
                  setError({})
                  setReason('')
                  setReasonModalOpen(null)
                },
              }}
              submit={{
                editid: null,
                label: 'Submit',
                loading: submitAPIStatus.loading,
                onClick: () =>
                  handleLeaveActions(
                    {
                      leaveId: reasonModalOpen?.leaveId ?? '',
                      status: reasonModalOpen?.status ?? '',
                      currentUser: currentUser,
                      reason: reason,
                    },
                    setReason,
                    setReasonModalOpen,
                    setError,
                    // setLeave,  //Hide bec after action the status is not updating properly, need to fetch the data again to update the status
                    () => {},   //Empty Arrow Function to avoid the error, need to fetch the data again to update the status
                    setAPIStatus,
                    setSubmitAPIStatus,
                    'receiver',
                    fetchData
                  ),
              }}
            />
          </div>
        </Modal>

        <div className='card mb-4' style={{borderRadius: '12px', border: '1px solid rgb(226, 236, 247)', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 3px', backgroundColor: 'rgb(255, 255, 255)'}}>
          <div className='card-header d-flex justify-content-between align-items-center' style={{backgroundColor: 'rgb(255, 255, 255)', borderBottom: '1px solid rgb(226, 236, 247)', padding: '8px 16px'}}>
            <h6 className='card-title mb-0' style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px'}}>Leave Filters</h6>
          </div>
          <div className='card-body' style={{padding: '8px 16px'}}>
            <div className='align-items-end row'>
              <div className='col-md-3'>
                <div className='mb-0'>
                  <label className='fw-bold mb-1 form-label' style={{color: 'rgb(18, 59, 115)', fontSize: '10px', fontWeight: '600'}}>Search</label>
                  <input
                    type="text"
                    placeholder="Search by name or code..."
                    className="form-control"
                    style={{borderRadius: '4px', border: '1px solid rgb(226, 236, 247)', padding: '4px 8px', fontSize: '11px'}}
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value)
                      setPagination((previous) => ({...previous, page: 1}))
                    }}
                  />
                </div>
              </div>
              <div className='col-md-3'>
                <div className='mb-0'>
                  <label className='fw-bold mb-1 form-label' style={{color: 'rgb(18, 59, 115)', fontSize: '10px', fontWeight: '600'}}>Status</label>
                  <select
                    className='form-select'
                    style={{borderRadius: '4px', border: '1px solid rgb(226, 236, 247)', padding: '4px 8px', fontSize: '11px'}}
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value)
                      setPagination((previous) => ({...previous, page: 1}))
                    }}
                  >
                    <option value='all'>All Status</option>
                    <option value='PENDING'>Pending</option>
                    <option value='REVIEW'>Review</option>
                    <option value='APPROVED'>Approved</option>
                    <option value='REJECTED'>Rejected</option>
                    <option value='CANCELLED'>Cancelled</option>
                    <option value='AUTO_APPROVED'>Auto Approved</option>
                    <option value='AUTO_REJECTED'>Auto Rejected (Historical)</option>
                  </select>
                </div>
              </div>
              <div className='col-md-3 d-flex align-items-end'>
                <button
                  type='button'
                  className='btn btn-light-primary btn-sm'
                  onClick={() => {
                    setSearchText('')
                    setSelectedStatus('all')
                    setSelectedUserId('all')
                    setPagination((previous) => ({...previous, page: 1}))
                    setShowRow([])
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='card' style={{borderRadius: '12px', border: '1px solid rgb(226, 236, 247)', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 3px', backgroundColor: 'rgb(255, 255, 255)', overflow: 'hidden'}}>
          <div className='card-header d-flex justify-content-between align-items-center' style={{backgroundColor: 'rgb(255, 255, 255)', borderBottom: '1px solid rgb(226, 236, 247)', padding: '12px 16px'}}>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              <h5 className='card-title mb-0' style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '14px'}}>My Applications</h5>
              <div className='d-flex gap-1' style={{backgroundColor: 'rgb(244, 248, 253)', padding: '3px', borderRadius: '6px'}}>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'all' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'all' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('all'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  All ({countableTotal})
                </button>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'PENDING' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'PENDING' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('PENDING'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  Pending ({statusCount('PENDING')})
                </button>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'APPROVED' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'APPROVED' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('APPROVED'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  Approved ({statusCount('APPROVED')})
                </button>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'REJECTED' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'REJECTED' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('REJECTED'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  Rejected ({statusCount('REJECTED')})
                </button>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'REVIEW' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'REVIEW' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('REVIEW'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  Review ({statusCount('REVIEW')})
                </button>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'CANCELLED' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'CANCELLED' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('CANCELLED'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  Cancelled ({statusCount('CANCELLED')})
                </button>
                <button
                  style={{
                    backgroundColor: selectedStatus === 'AUTO_APPROVED' ? 'rgb(59, 130, 246)' : 'transparent',
                    color: selectedStatus === 'AUTO_APPROVED' ? 'rgb(255, 255, 255)' : 'rgb(100, 116, 139)',
                    border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', cursor: 'pointer', transition: '0.2s'
                  }}
                  onClick={() => { setSelectedStatus('AUTO_APPROVED'); setPagination((previous) => ({...previous, page: 1})) }}
                >
                  Auto Approved ({statusCount('AUTO_APPROVED')})
                </button>
              </div>
            </div>
          </div>
          <div className='card-body' style={{padding: '0px'}}>
            {requestedLeaveUnavailable && (
              <div className='alert alert-warning m-4' role='alert'>
                This leave request is no longer available or you do not have permission to view it.
              </div>
            )}
            {apiStatus.loading ? (
              <Center py='xl'>Loading leave applications...</Center>
            ) : apiStatus.error ? (
              <div className='alert alert-danger m-4' role='alert'>
                <div>{apiStatus.error}</div>
                <button type='button' className='btn btn-sm btn-light-danger mt-3' onClick={() => fetchData()}>Try again</button>
              </div>
            ) : leave?.length > 0 ? (
              <div style={{overflowX: 'auto', maxWidth: '100%'}}>
                <table style={{width: '100%', minWidth: '800px', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: 'rgb(255, 255, 255)'}}>
                  <thead>
                    <tr style={{backgroundColor: 'rgb(244, 248, 253)'}}>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '8%'}}></th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '15%'}}>Name</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '12%'}}>Employee Code</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '12%'}}>Subject</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '15%'}}>Reason</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '10%'}}>Status</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '12%'}}>Create Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leave.map((obj: any, index: number) => (
                      <>
                        <tr id={`leave-application-${obj?.id}`} style={{borderBottom: '1px solid rgb(226, 236, 247)', transition: 'background-color 0.2s', backgroundColor: 'transparent'}}>
                          <td style={{padding: '10px 16px', verticalAlign: 'middle'}}>
                            <span
                              style={{
                                backgroundColor: 'rgb(234, 244, 255)',
                                color: 'rgb(59, 130, 246)',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={() => {
                                checkArrayElement(index)
                              }}
                            >
                              {showRow.includes(index) ? '▲' : '▼'}
                            </span>
                          </td>
                          <td style={{padding: '10px 16px', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', verticalAlign: 'middle'}}>
                            {obj?.user?.firstName ?? '-'} {obj?.user?.lastName ?? '-'}
                          </td>
                          <td style={{padding: '10px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle'}}>
                            {obj?.user?.employeeCode ?? '-'}
                          </td>
                          <td style={{padding: '10px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle'}}>
                            {`Request for ${obj?.leaveType?.name}`}
                          </td>
                          <td
                            style={{padding: '10px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle', cursor: 'pointer'}}
                            onClick={() => {
                              const reasonText = [
                                obj?.reason?.label,
                                obj?.description
                              ].filter(Boolean).join(' - ') || '-';
                              setReasonRemarkModalOpen(
                                reasonText?.replaceAll('<p>', '')?.replaceAll('</p>', '')
                              )
                              setSelectedColumn('Reason')
                            }}
                          >
                            {truncateString(
                              [
                                obj?.reason?.label,
                                obj?.description
                              ].filter(Boolean).join(' - ')?.replaceAll('<p>', '')?.replaceAll('</p>', '') || '-',
                              25
                            )}
                          </td>
                          <td style={{padding: '10px 16px', verticalAlign: 'middle'}}>
                            <span
                              style={{
                                backgroundColor: ['APPROVED', 'AUTO_APPROVED'].includes(obj?.status)
                                  ? 'rgb(240, 253, 244)'
                                  : obj?.status === 'REJECTED'
                                    ? 'rgb(254, 242, 242)'
                                    : obj?.status === 'CANCELLED'
                                      ? 'rgb(243, 244, 246)'
                                      : 'rgb(255, 251, 235)',
                                color: ['APPROVED', 'AUTO_APPROVED'].includes(obj?.status)
                                  ? 'rgb(22, 163, 74)'
                                  : obj?.status === 'REJECTED'
                                    ? 'rgb(239, 68, 68)'
                                    : obj?.status === 'CANCELLED'
                                      ? 'rgb(100, 116, 139)'
                                      : 'rgb(217, 119, 6)',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: '600',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {obj?.status === 'AUTO_APPROVED' ? 'AUTO APPROVED' : obj?.status}
                            </span>
                          </td>
                          <td style={{padding: '10px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle'}}>
                            {formatCreateAt(obj?.createdAt ?? '-')}
                          </td>
                        </tr>

                        <tr key={`secondIndex${index}`} hidden={!showRow.includes(index)}>
                          <td colSpan={7} style={{padding: '0px', backgroundColor: 'rgb( 248, 250, 252)'}}>
                            <div style={{padding: '16px'}}>
                              <table className='table table-bordered' style={{backgroundColor: 'rgb(255, 255, 255)', borderColor: 'rgb(226, 236, 247)', borderCollapse: 'collapse', width: '100%'}}>
                                <thead>
                                  <tr style={{backgroundColor: 'rgb(244, 248, 253)'}}>
                                    <th style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '11px', padding: '10px 12px', borderBottom: '1px solid rgb(226, 236, 247)', textAlign: 'left'}}>Type</th>
                                    <th style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '11px', padding: '10px 12px', borderBottom: '1px solid rgb(226, 236, 247)', textAlign: 'left'}}>Start Date</th>
                                    <th style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '11px', padding: '10px 12px', borderBottom: '1px solid rgb(226, 236, 247)', textAlign: 'left'}}>End Date</th>
                                    <th style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '11px', padding: '10px 12px', borderBottom: '1px solid rgb(226, 236, 247)', textAlign: 'left'}}>Attachment</th>
                                    <th style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '11px', padding: '10px 12px', borderBottom: '1px solid rgb(226, 236, 247)', textAlign: 'left'}}>Status</th>
                                    <th style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '11px', padding: '10px 12px', borderBottom: '1px solid rgb(226, 236, 247)', textAlign: 'left'}}>Action By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td style={{padding: '10px 12px', color: 'rgb(100, 116, 139)', fontSize: '11px'}}>{changeTextCamal(obj.leaveDuration)}</td>
                                    <td style={{padding: '10px 12px', color: 'rgb(100, 116, 139)', fontSize: '11px'}}>{formatDates(obj?.startDate ?? '-')}</td>
                                    <td style={{padding: '10px 12px', color: 'rgb(100, 116, 139)', fontSize: '11px'}}>{formatDates(obj?.endDate ?? '-')}</td>
                                    <td style={{padding: '10px 12px', color: 'rgb(100, 116, 139)', fontSize: '11px'}}>
                                      {obj?.attachment ? (
                                        <Button
                                          variant='outline'
                                          color='blue'
                                          size='xs'
                                          onClick={() => {
                                            const root = (process.env.REACT_APP_API_KEY || '').replace(/\/api\/v1\/?$/, '');
                                            const attachmentUrl = `${root}/storage/leave/${obj?.attachment}`;
                                            window.open(attachmentUrl, '_blank');
                                          }}
                                        >
                                          <i className='fa-solid fa-eye'></i>
                                        </Button>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td style={{padding: '10px 12px'}}>
                                      {['PENDING', 'REVIEW']?.includes(obj?.status) ? (
                                        <div style={{display: 'flex', gap: '4px'}}>
                                          <button
                                            className='btn btn-icon btn-light-primary btn-sm'
                                            style={{padding: '4px 8px', borderRadius: '4px', fontSize: '11px'}}
                                            onClick={() =>
                                              setReasonModalOpen({
                                                leaveId: obj?.id,
                                                status: 'APPROVED',
                                              })
                                            }
                                          >
                                            <i className='fa-solid fa-check'></i>
                                          </button>
                                          <button
                                            className='btn btn-icon btn-light-danger btn-sm'
                                            style={{padding: '4px 8px', borderRadius: '4px', fontSize: '11px'}}
                                            onClick={() =>
                                              setReasonModalOpen({
                                                leaveId: obj?.id,
                                                status: 'REJECTED',
                                              })
                                            }
                                          >
                                            <i className='fa-solid fa-xmark'></i>
                                          </button>
                                        </div>
                                      ) : (
                                        <span
                                          style={{
                                            backgroundColor: obj?.status === 'APPROVED'
                                              ? 'rgb(240, 253, 244)'
                                              : obj?.status === 'REJECTED'
                                                ? 'rgb(254, 242, 242)'
                                                : 'rgb(243, 244, 246)',
                                            color: obj?.status === 'APPROVED'
                                              ? 'rgb(22, 163, 74)'
                                              : obj?.status === 'REJECTED'
                                                ? 'rgb(239, 68, 68)'
                                                : 'rgb(100, 116, 139)',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            display: 'inline-block'
                                          }}
                                        >
                                          {obj?.status}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{padding: '10px 12px', color: 'rgb(100, 116, 139)', fontSize: '11px'}}>
                                      {obj?.status === 'CANCELLED' ? (
                                        <>
                                          <div>{obj?.user?.firstName ?? '-'} {obj?.user?.lastName ?? '-'}</div>
                                          <div className='text-muted' style={{fontSize: '10px'}}>
                                            {obj?.updatedAt ? formatCreateAt(obj?.updatedAt ?? '-') : '-'}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          {obj?.LeaveApprover?.filter((item) => item?.status !== 'PENDING')?.map(
                                            (item, index) =>
                                              index === 0 && (
                                                <>
                                                  <div>{item?.User?.firstName ?? '-'} {item?.User?.lastName ?? '-'}</div>
                                                  <div className='text-muted' style={{fontSize: '10px'}}>
                                                    {item?.updatedAt ? formatCreateAt(item?.updatedAt ?? '-') : '-'}
                                                  </div>
                                                </>
                                              )
                                          )}
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{padding: '80px 24px', textAlign: 'center', backgroundColor: 'rgb(255, 255, 255)'}}>
                <div style={{backgroundColor: 'rgb(244, 248, 253)', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0px auto 20px'}}>
                  <i className='fa fa-file-alt fa-2x' style={{color: 'rgb(100, 116, 139)', fontSize: '32px'}}></i>
                </div>
                <div style={{color: 'rgb(18, 59, 115)', fontSize: '18px', fontWeight: '600', marginBottom: '8px'}}>No leave applications found</div>
                <div style={{color: 'rgb(100, 116, 139)', fontSize: '14px'}}>Try adjusting your filters</div>
              </div>
            )}
          </div>
          <div className='px-4 pb-4'>
            <ListPagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalRecords={pagination.total}
              disabled={apiStatus.loading}
              onPageChange={(page) => setPagination((previous) => ({...previous, page}))}
              onPageSizeChange={(pageSize) => setPagination((previous) => ({...previous, page: 1, pageSize}))}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Applications
